import { IsArray, IsOptional, IsString } from 'class-validator';
import { ICustomerUser } from './customer.dto';
import {
  IProductSubscriptionPhase,
  IProductV2Option,
  ProductV2Image,
  ProductV2Status,
  RelatedProduct
} from './productV2.dto';
import { IProductCategory } from './product-category.dto';
import { type Types } from 'mongoose';
import type { IProviderPrescribingContext } from './order.dto';
import type { IPharmacyAddress } from './common.dto';
export type CartV2Status = 'pending';
export type CartTheme = {
  fontFamily?: string;
  primaryColor?: string;
  cartSummarySection?: string;
};

/** Known cart `extras` keys (JJC transfer-out flags + checkout pharmacy/med-team state). */
export interface ICartV2Extras {
  needsMedicalTeamContact?: boolean;
  pharmacyId?: string;
  pharmacyAddress?: IPharmacyAddress;
  transferOut?: boolean;
  /** Set when this service auto-inserted the fee line (cleared on disable). */
  transferOutManagedFeeLineId?: string;
  /** False when every medication is in `transferOutFeeExemptCategoryIds`. */
  transferOutChargesFlatFee?: boolean;
  [key: string]: unknown;
}

export class ICartV2 {
  _id?: string;
  organization: string;
  customer?: string | ICustomerUser | Types.ObjectId;
  lineItems: ICartV2LineItem[];
  discountAmount: number; // this is a positive integer representing the total discount amount
  baseAmount: number; // this is the total amount based on item list prices
  subTotalAmount: number; // this is the total amount before discounts (promotions + coupons)
  totalAmount: number; // this is the payable amount (i.e. baseAmount - discountAmount)
  postPurchaseCredit: number; // amount of credit to be added to customer account after purchase
  extras?: ICartV2Extras;
  meta: any;
  createdAt?: Date;
  updatedAt?: Date;
  discountType?: 'cart' | 'product';
  paymentIntentId?: string;
  orderCreationClaimedAt?: Date; // set while one caller is turning this cart into orders
  delayedCaptureAmount?: number; // if every item in the cart is delayed capture
  appliedPromotions?: string[]; // list of promotion ids applied to this cart
}

export class ICartV2LineItem {
  id: string;
  name: string;
  images?: ProductV2Image[];
  thumbnailUrl?: string;
  subLabel?: string;
  description: string;
  productId: string | number;
  variantId: number | string;
  sku: string;
  productStatus: ProductV2Status;
  /** final price after discounts */
  price: number;
  /** original price before discounts */
  listPrice: number;
  optionId: number | string;
  optionType: number;
  optionLabel: string;
  quantity: number;
  // TODO add this to product dto
  // discuss with @adeeb
  contradictingProducts?: string[];
  imageUrl?: string;
  url?: string;
  isSubscription: boolean;
  subscriptionPhases?: IProductSubscriptionPhase[];
  relatedProducts?: RelatedProduct[];
  extras: any;
  type: 'physical' | 'digital';
  discount?: ILineItemDiscount;
  options?: IProductV2Option[];
  categories?: (IProductCategory | Types.ObjectId)[];
  postPurchaseCredit?: number;
  delayedCaptureAmount?: number; // if the specific item is delayed capture
  vendor?: string;
  isNonPrescription?: boolean;
  allowBNPL?: boolean;
  providerPrescribingContext?: IProviderPrescribingContext;
}

/** One stacked slice of discount on a line (e.g. regular + delayed-capture promotion). */
export class ILineItemDiscountComponent {
  amount: number;
  reductionAmount: number;
  reductionType: 'percent' | 'dollar';
  type: 'coupon' | 'promotion' | 'manual';
  referenceId: string;
  name?: string;
}

export class ILineItemDiscount {
  amount: number; // the dollar discount amount (max discount is the item price)
  reductionAmount: number; // the discount amount in the coupon whether it's percent or dollar
  reductionType: 'percent' | 'dollar';
  type: 'coupon' | 'promotion' | 'manual';
  referenceId: string; // coupon code or promotion id (latest applied when stacked)
  name?: string;
  /** When multiple discounts apply to the same line, each row; amounts should sum to `amount`. */
  components?: ILineItemDiscountComponent[];
}

export class ICartV2LineItemUnpopulated {
  id?: string;
  productId: string | number;
  variantId: number | string;
  quantity: number;
  options: Record<string, string | number>;
  providerPrescribingContext?: IProviderPrescribingContext;
  /**
   * Optional persisted sale price. Ignored on populate: transfer-out $0 is derived
   * from cart extras so a leftover override cannot strand a line at $0.
   */
  price?: number;
}

export class CreateCartV2Dto {
  @IsOptional()
  @IsString()
  organization?: string;

  @IsOptional()
  @IsString()
  customer?: string;

  @IsOptional()
  @IsString()
  status?: CartV2Status;

  @IsArray()
  lineItems: ICartV2LineItemUnpopulated[];
}

export class UpdateCartDto {
  @IsString()
  status: CartV2Status;

  @IsOptional()
  @IsArray()
  lineItems?: ICartV2LineItemUnpopulated[];
}
